import * as React from "react";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import "./TreeCard.css";

export default function TreeCard() {
  // Structured data for SEO
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Christmas Tree Varieties",
    "description": "A comprehensive guide to different types of Christmas trees including Noble Fir, Douglas Fir, Fraser Fir, and more.",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "Product",
          "name": "Noble Fir Christmas Tree",
          "description": "Known for its tall and narrow shape, sturdy branches, and bluish-green needles with silvery-white appearance. Excellent for heavy ornaments.",
          "image": "images/Noble.jpg"
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "Product",
          "name": "Douglas Fir Christmas Tree",
          "description": "Features soft, dark green needles with sweet fragrance. Popular choice for its durability and pleasant aroma.",
          "image": "images/Douglas.jpg"
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "Product",
          "name": "Silvertip Fir Christmas Tree",
          "description": "Strikingly beautiful silvery-blue foliage with sweet, citrusy scent. Distinctive upward-sweeping branches.",
          "image": "images/Silvertip.jpg"
        }
      },
      {
        "@type": "ListItem",
        "position": 4,
        "item": {
          "@type": "Product",
          "name": "Nordman Fir Christmas Tree",
          "description": "Symmetrical shape with glossy, deep green needles. Excellent needle retention and long shelf life.",
          "image": "images/Nordman.jpg"
        }
      },
      {
        "@type": "ListItem",
        "position": 5,
        "item": {
          "@type": "Product",
          "name": "Fraser Fir Christmas Tree",
          "description": "Dark green needles with silvery underside and pleasant balsam fragrance. Exceptional needle retention.",
          "image": "images/Fraser.jpg"
        }
      },
      {
        "@type": "ListItem",
        "position": 6,
        "item": {
          "@type": "Product",
          "name": "Grand Fir Christmas Tree",
          "description": "Tall stature with lustrous green needles and distinctive citrusy scent. Known for fire-resistant properties.",
          "image": "https://i.imgur.com/psKUrqw.jpg"
        }
      }
    ]
  };

  return (
    <>
      {/* Structured Data JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>

      {/* Main Section with Semantic HTML */}
      <section className="tree-card-container" aria-label="Christmas Tree Varieties Guide">
        {/* Hidden H1 for SEO - describes page content */}
        <h1 className="visually-hidden">
          Christmas Tree Types: Complete Guide to Noble, Douglas, Fraser, Silvertip, Nordman, and Grand Fir
        </h1>

        {/* Noble Fir Card */}
        <Card 
          component="article" 
          className="Card" 
          sx={{ boxShadow: 5, borderRadius: "24px", display: 'flex', flexDirection: 'column' }}
          itemScope 
          itemType="https://schema.org/Product"
        >
          <CardMedia
            component="img"
            sx={{ height: "400px", objectFit: "cover" }}
            image="images/Noble.jpg"
            alt="Noble Fir Christmas tree showing tall narrow shape with bluish-green needles and silvery-white appearance"
            title="Noble Fir Christmas Tree"
            loading="lazy"
            itemProp="image"
          />
          <CardContent>
            <Typography
              gutterBottom
              component="h2"
              sx={{ fontSize: "24px" }}
              itemProp="name"
            >
              Noble Fir
            </Typography>
            <Typography variant="body2" color="text.dark" itemProp="description">
              The Noble Fir is a species of evergreen tree that is native to the Pacific Northwest region of North America. It is known for its tall and narrow shape, sturdy branches, and bluish-green needles that have a silvery-white appearance. The needles are also densely packed and have a distinct fragrance that is often described as refreshing and invigorating. The Noble Fir is a popular choice for Christmas trees and wreaths due to its attractive appearance, long needle retention, and ability to hold heavy ornaments. Additionally, the tree is known for its excellent resistance to pests and disease, making it a durable and reliable choice for holiday decorations.
            </Typography>
          </CardContent>
          <CardActions className="Cardbtn">
            <Button
              size="small"
              href="https://i.imgur.com/4OiR7Qp.jpg"
              target="_blank"
              rel="noopener noreferrer"
              color="secondary"
              className="home-grid-button"
              aria-label="View close-up photo of Noble Fir branches and needles"
            >
              Click to see branches
            </Button>
          </CardActions>
        </Card>

        {/* Douglas Fir Card */}
        <Card 
          component="article" 
          className="Card" 
          sx={{ boxShadow: 5, borderRadius: "24px", display: 'flex', flexDirection: 'column' }}
          itemScope 
          itemType="https://schema.org/Product"
        >
          <CardMedia
            component="img"
            sx={{ height: "400px", objectFit: "cover" }}
            image="images/Douglas.jpg"
            alt="Douglas Fir Christmas tree with soft dark green needles and conical shape"
            title="Douglas Fir Christmas Tree"
            loading="lazy"
            itemProp="image"
          />
          <CardContent>
            <Typography
              gutterBottom
              component="h2"
              sx={{ fontSize: "24px" }}
              itemProp="name"
            >
              Douglas Fir
            </Typography>
            <Typography variant="body2" color="text.dark" itemProp="description">
              The Douglas Fir is a species of evergreen tree that is native to western North America. It is known for its tall and straight trunk, conical shape, and soft, dark green needles that have a sweet fragrance. The Douglas Fir is a popular choice for Christmas trees and other decorative uses due to its attractive appearance, durability, and pleasant aroma. The tree also has a good needle retention rate and can withstand dry indoor environments, making it a practical choice for holiday decorations. Additionally, the Douglas Fir is an important timber species and provides important habitat for a variety of wildlife species in its native range.
            </Typography>
          </CardContent>
          <CardActions className="Cardbtn">
            <Button
              size="small"
              href="https://i.imgur.com/LlIDjFP.jpg"
              target="_blank"
              rel="noopener noreferrer"
              color="secondary"
              className="home-grid-button"
              aria-label="View close-up photo of Douglas Fir branches and needles"
            >
              Click to see branches
            </Button>
          </CardActions>
        </Card>

        {/* Silvertip Fir Card */}
        <Card 
          component="article" 
          className="Card" 
          sx={{ boxShadow: 5, borderRadius: "24px", display: 'flex', flexDirection: 'column' }}
          itemScope 
          itemType="https://schema.org/Product"
        >
          <CardMedia
            component="img"
            sx={{ height: "400px", objectFit: "cover" }}
            image="./images/Silvertip.jpg"
            alt="Silvertip Fir Christmas tree with distinctive silvery-blue foliage and upward-sweeping branches"
            title="Silvertip Fir Christmas Tree"
            loading="lazy"
            itemProp="image"
          />
          <CardContent>
            <Typography
              gutterBottom
              component="h2"
              sx={{ fontSize: "24px" }}
              itemProp="name"
            >
              Silvertip Fir
            </Typography>
            <Typography variant="body2" color="text.dark" itemProp="description">
              The Silvertip Fir is a species of evergreen tree that is native to the mountains of California and Oregon in the United States. It is known for its strikingly beautiful silvery-blue foliage that is soft to the touch and has a sweet, citrusy scent. The Silvertip Fir typically grows to be a tall and slender tree, with a narrow crown and upward-sweeping branches that give it a distinctive appearance. Due to its unique beauty, the Silvertip Fir is a popular choice for Christmas trees and other decorative uses.
            </Typography>
          </CardContent>
          <CardActions className="Cardbtn">
            <Button
              size="small"
              href="https://i.imgur.com/GnEzmIb.jpg"
              target="_blank"
              rel="noopener noreferrer"
              color="secondary"
              className="home-grid-button"
              aria-label="View close-up photo of Silvertip Fir branches and needles"
            >
              Click to see branches
            </Button>
          </CardActions>
        </Card>

        {/* Nordman Fir Card */}
        <Card 
          component="article" 
          className="Card" 
          sx={{ boxShadow: 5, borderRadius: "24px", display: 'flex', flexDirection: 'column' }}
          itemScope 
          itemType="https://schema.org/Product"
        >
          <CardMedia
            component="img"
            sx={{ height: "400px", objectFit: "cover" }}
            image="./images/Nordman.jpg"
            alt="Nordman Fir Christmas tree with symmetrical shape and glossy deep green needles"
            title="Nordman Fir Christmas Tree"
            loading="lazy"
            itemProp="image"
          />
          <CardContent>
            <Typography
              gutterBottom
              component="h2"
              sx={{ fontSize: "24px" }}
              itemProp="name"
            >
              Nordman Fir
            </Typography>
            <Typography variant="body2" color="text.dark" itemProp="description">
              The Nordman Fir is a species of evergreen tree that is native to the mountains of the Caucasus region in Eurasia. It is known for its symmetrical shape, full branches, and glossy, deep green needles that are soft to the touch and have a mild fragrance. The Nordman Fir is a popular choice for Christmas trees and other decorative uses because of its dense and attractive foliage, which holds its needles well and has a long shelf life. It is also a hardy and adaptable tree that can thrive in a variety of environments.
            </Typography>
          </CardContent>
          <CardActions className="Cardbtn">
            <Button
              size="small"
              href="https://i.imgur.com/fywccnV.jpg"
              target="_blank"
              rel="noopener noreferrer"
              color="secondary"
              className="home-grid-button"
              aria-label="View close-up photo of Nordman Fir branches and needles"
            >
              Click to see branches
            </Button>
          </CardActions>
        </Card>

        {/* Fraser Fir Card */}
        <Card 
          component="article" 
          className="Card" 
          sx={{ boxShadow: 5, borderRadius: "24px", display: 'flex', flexDirection: 'column' }}
          itemScope 
          itemType="https://schema.org/Product"
        >
          <CardMedia
            component="img"
            sx={{ height: "400px", objectFit: "cover" }}
            image="images/Fraser.jpg"
            alt="Fraser Fir Christmas tree with dark green needles featuring distinctive silvery underside"
            title="Fraser Fir Christmas Tree"
            loading="lazy"
            itemProp="image"
          />
          <CardContent>
            <Typography
              gutterBottom
              component="h2"
              sx={{ fontSize: "24px" }}
              itemProp="name"
            >
              Fraser Fir
            </Typography>
            <Typography variant="body2" color="text.dark" itemProp="description">
              The Fraser Fir is a species of evergreen tree that is native to the Appalachian Mountains in the eastern United States. It is known for its conical shape, dense branches, and dark green needles that have a silvery underside. The needles are soft to the touch and have a pleasant, balsam-like fragrance, making it a popular choice for Christmas trees and wreaths. The Fraser Fir also has an exceptional needle retention rate, even in dry indoor environments, which makes it a practical and durable choice for holiday decorations. Additionally, the tree is known for its resistance to pests and disease.
            </Typography>
          </CardContent>
          <CardActions className="Cardbtn">
            <Button
              size="small"
              href="https://i.imgur.com/Dot4wjJ.jpg"
              target="_blank"
              rel="noopener noreferrer"
              color="secondary"
              className="home-grid-button"
              aria-label="View close-up photo of Fraser Fir branches and needles"
            >
              Click to see branches
            </Button>
          </CardActions>
        </Card>

        {/* Grand Fir Card */}
        <Card 
          component="article" 
          className="Card" 
          sx={{ boxShadow: 5, borderRadius: "24px", display: 'flex', flexDirection: 'column' }}
          itemScope 
          itemType="https://schema.org/Product"
        >
          <CardMedia
            component="img"
            sx={{ height: "400px", objectFit: "cover" }}
            image="https://i.imgur.com/psKUrqw.jpg"
            alt="Grand Fir Christmas tree with lustrous green needles and distinctive citrusy scent"
            title="Grand Fir Christmas Tree"
            loading="lazy"
            itemProp="image"
          />
          <CardContent>
            <Typography
              gutterBottom
              component="h2"
              sx={{ fontSize: "24px" }}
              itemProp="name"
            >
              Grand Fir
            </Typography>
            <Typography variant="body2" color="text.dark" itemProp="description">
              The Grand Fir is a species of evergreen tree that is native to the Pacific Northwest region of North America. It is known for its tall stature, thick and full branches, and soft, lustrous green needles that have a distinctive citrusy scent. The Grand Fir is a popular choice for Christmas trees and wreaths due to its attractive appearance, long needle retention, and pleasant aroma. The tree is also known for its fire-resistant properties, making it a safe and reliable choice for holiday decorations. In addition, the Grand Fir provides important habitat for a variety of wildlife species in its native range.
            </Typography>
          </CardContent>
          <CardActions className="Cardbtn">
            <Button
              size="small"
              href="https://i.imgur.com/mmWwALs.jpg"
              target="_blank"
              rel="noopener noreferrer"
              color="secondary"
              className="home-grid-button"
              aria-label="View close-up photo of Grand Fir branches and needles"
            >
              Click to see branches
            </Button>
          </CardActions>
        </Card>
      </section>
    </>
  );
}